import React, { useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import { GenericBarChart } from '@/components/shared/GenericBarChart';
import { scrollDesign } from '../../styles';
import { CustomLegend } from './CustomLegend';
import {
  DefectsAreaForCustomBlocksQuery,
  NameAndArea,
  PaintBlocksCorrodedArea,
  UnitSystemEnum,
} from '@/__generated__/graphql';
import { useAtomValue } from 'jotai';
import { Label } from 'recharts';
import * as state from '@/components/Analysis/state';
import { abyssColors } from '@/theme/colors';
import { convertDefects } from './utils';

type Props = {
  coatingConditionLegend: { [key: string]: string };
  defectsAreaForPaintBlockAndDecks: PaintBlocksCorrodedArea | undefined;
  defectAreaForCustomBlocks:
    | DefectsAreaForCustomBlocksQuery['defectsAreaForAssemblyPaintRegionsByGroup']
    | undefined;
  selectedConditionType: string;
  graphsContainerWidth: number;
  selectedCoatingCondition: string[];
};

export const DefectsGraphBase = ({
  coatingConditionLegend,
  defectsAreaForPaintBlockAndDecks,
  selectedConditionType,
  selectedCoatingCondition,
  graphsContainerWidth,
  defectAreaForCustomBlocks,
}: Props) => {
  const unitSystem = useAtomValue(state.unitSystem);
  const selectedPaintRegionGroup = useAtomValue(state.selectedPaintRegionGroup);

  const defectsGraphData = useMemo(() => {
    return {
      deck: convertDefects(
        defectsAreaForPaintBlockAndDecks?.defectsByDeck || [],
        selectedCoatingCondition
      ),
      customBlocks: convertDefects(
        (defectAreaForCustomBlocks?.defectsByPaintBlock || []) as NameAndArea[],
        selectedCoatingCondition
      ),
      paintBlock: convertDefects(
        defectsAreaForPaintBlockAndDecks?.defectsByPaintBlock || [],
        selectedCoatingCondition
      ),
    };
  }, [
    defectAreaForCustomBlocks?.defectsByPaintBlock,
    defectsAreaForPaintBlockAndDecks?.defectsByDeck,
    defectsAreaForPaintBlockAndDecks?.defectsByPaintBlock,
    selectedCoatingCondition,
  ]);

  const labelValue = useMemo(() => {
    return (
      <Label
        offset={20}
        position="top"
        style={{
          fontSize: '1.1rem',
          fill: abyssColors.primary[400],
        }}
        opacity={0.8}
        value={unitSystem === UnitSystemEnum.Imperial ? '(ft²)' : '(m²)'}
      />
    );
  }, [unitSystem]);

  // if the default filter is selected, fetch the regular paint blocks
  // instead of the custom blocks
  if (!selectedPaintRegionGroup || selectedPaintRegionGroup?.isDefault)
    return (
      <Stack direction="column" gap={2} mb={2}>
        <Stack direction="row" spacing={2}>
          <Box sx={{ width: '30%', ...scrollDesign }} data-testid="area-by-deck-chart">
            <GenericBarChart
              title={`${selectedConditionType} by deck`}
              data={defectsGraphData.deck}
              legendData={coatingConditionLegend}
              containerWidth={graphsContainerWidth}
              removeZeroValueRecords
              hideLegend
              customLabel={labelValue}
            />
          </Box>
          <Box sx={{ width: '70%', ...scrollDesign }} data-testid="area-by-block-chart">
            <GenericBarChart
              title={`${selectedConditionType} by paint block`}
              data={defectsGraphData.paintBlock}
              legendData={coatingConditionLegend}
              showRecordsLimit
              containerWidth={graphsContainerWidth}
              removeZeroValueRecords
              hideLegend
              customLabel={labelValue}
            />
          </Box>
        </Stack>
        <CustomLegend data={coatingConditionLegend} />
      </Stack>
    );

  return (
    <Box sx={{ width: '100%', ...scrollDesign }} data-testid="area-by-deck-chart">
      <GenericBarChart
        title={`${selectedConditionType} by ${selectedPaintRegionGroup?.name || ''} custom block`}
        data={defectsGraphData.customBlocks}
        legendData={coatingConditionLegend}
        containerWidth={graphsContainerWidth}
        removeZeroValueRecords
        hideLegend
        showRecordsLimit
        customLabel={labelValue}
      />
    </Box>
  );
};

export const DefectsGraph = React.memo(DefectsGraphBase);
